import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { Numeric } from '@/lib/types/common';
import { EMarketAction, MarketAction } from '@/lib/types/market-action';
import { MarketIntent } from '@/lib/types/market-intent';
import { EOrderBookSide, OrderBookItem, OrderBookSide } from '@/lib/types/order-book';

const getCumulativeSharesCount = (side: OrderBookSide, orders: OrderBookItem[], price: Decimal) => {
  if (side === EOrderBookSide.ASKS) {
    return orders
      .filter((o) => o.price.lessThanOrEqualTo(price))
      .reduce((acc, o) => {
        return acc.plus(o.size);
      }, Decimal(0));
  } else {
    return orders
      .filter((o) => o.price.greaterThanOrEqualTo(price))
      .reduce((acc, o) => {
        return acc.plus(o.size);
      }, Decimal(0));
  }
};

type UseMatchingSharesAtPriceParams = {
  outcomeId: string;
  intent: MarketIntent;
  action: MarketAction;
  price: Numeric;
};

// cumulative shares count up to the specified price
export const useMatchingSharesAtPrice = ({
  outcomeId,
  intent,
  action,
  price,
}: UseMatchingSharesAtPriceParams): Decimal => {
  const effectiveOrderBook = useOrderBookDataById(outcomeId);

  return useMemo(() => {
    if (!effectiveOrderBook) {
      return Decimal(0);
    }
    const { asks, bids } = effectiveOrderBook[intent];

    if (action === EMarketAction.BUY) {
      return getCumulativeSharesCount(EOrderBookSide.ASKS, asks, Decimal(price));
    }
    if (action === EMarketAction.SELL) {
      return getCumulativeSharesCount(EOrderBookSide.BIDS, bids, Decimal(price));
    }
    return Decimal(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent, action, price, effectiveOrderBook]);
};
